import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Input, Label, Textarea } from '@/components/ui'
import { CreditCard, CheckCircle2, XCircle, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

interface ValidationResult {
  card: string
  valid: boolean
}

export function LuhnValidator() {
  const [input, setInput] = useState('')
  const [results, setResults] = useState<ValidationResult[]>([])

  const validateLuhn = (cardNumber: string): boolean => {
    // Remove spaces and dashes
    const cleaned = cardNumber.replace(/[\s-]/g, '')

    // Check if it's only digits
    if (!/^\d+$/.test(cleaned)) return false

    // Luhn algorithm
    let sum = 0
    let isEven = false

    for (let i = cleaned.length - 1; i >= 0; i--) {
      let digit = parseInt(cleaned[i], 10)

      if (isEven) {
        digit *= 2
        if (digit > 9) {
          digit -= 9
        }
      }

      sum += digit
      isEven = !isEven
    }

    return sum % 10 === 0
  }

  const getCardType = (cardNumber: string): string => {
    const cleaned = cardNumber.replace(/[\s-]/g, '')

    if (/^4/.test(cleaned)) return 'Visa'
    if (/^5[1-5]/.test(cleaned)) return 'Mastercard'
    if (/^3[47]/.test(cleaned)) return 'Amex'
    if (/^6(?:011|5)/.test(cleaned)) return 'Discover'
    if (/^35(?:2[89]|[3-8])/.test(cleaned)) return 'JCB'
    if (/^3(?:0[0-5]|[68])/.test(cleaned)) return 'Diners'

    return 'Unknown'
  }

  const handleValidate = () => {
    if (!input.trim()) {
      toast.error('Please enter card number(s)')
      return
    }

    const lines = input.split('\n').filter(line => line.trim())
    const validationResults: ValidationResult[] = lines.map(line => {
      // Extract card number (handle formats like "4111111111111111|12|2025|123")
      const cardNumber = line.split('|')[0].trim()
      return {
        card: cardNumber,
        valid: validateLuhn(cardNumber)
      }
    })

    setResults(validationResults)

    const validCount = validationResults.filter(r => r.valid).length
    const invalidCount = validationResults.length - validCount

    toast.success(`Validated: ${validCount} valid, ${invalidCount} invalid`)
  }

  const clearAll = () => {
    setInput('')
    setResults([])
    toast.success('Cleared')
  }

  const validCount = results.filter(r => r.valid).length
  const invalidCount = results.length - validCount

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-purple-500/20">
            <CreditCard className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Card Luhn Validator</h1>
            <p className="text-muted-foreground">Validate card numbers using Luhn algorithm</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="glass">
          <CardHeader>
            <CardTitle>Input</CardTitle>
            <CardDescription>Enter card numbers (one per line)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Card Numbers</Label>
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Enter card numbers...&#10;&#10;Supports formats:&#10;4111111111111111&#10;4111-1111-1111-1111&#10;4111111111111111|12|2025|123"
                className="font-mono min-h-[250px]"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleValidate} className="flex-1">
                Validate
              </Button>
              <Button variant="outline" onClick={clearAll}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="glass">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Results</CardTitle>
                <CardDescription>Validation results</CardDescription>
              </div>
              {results.length > 0 && (
                <div className="flex gap-2">
                  <span className="text-xs px-2 py-1 rounded bg-green-500/20 text-green-500">
                    {validCount} valid
                  </span>
                  <span className="text-xs px-2 py-1 rounded bg-red-500/20 text-red-500">
                    {invalidCount} invalid
                  </span>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {results.length > 0 ? (
              <div className="space-y-2 max-h-[300px] overflow-y-auto custom-scrollbar">
                {results.map((result, index) => (
                  <div
                    key={index}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      result.valid ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {result.valid ? (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-500" />
                      )}
                      <div>
                        <p className="font-mono text-sm">{result.card}</p>
                        <p className="text-xs text-muted-foreground">{getCardType(result.card)}</p>
                      </div>
                    </div>
                    <span className={`text-xs font-medium ${result.valid ? 'text-green-500' : 'text-red-500'}`}>
                      {result.valid ? 'VALID' : 'INVALID'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <CreditCard className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Enter card numbers and click Validate</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
